import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { api } from './api';
import type { Profile } from './types';

type AuthValue = {
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  startupError: string;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
  hasRole: (...roles: string[]) => boolean;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [startupError, setStartupError] = useState('');

  const refreshProfile = async () => {
    const current = (await supabase.auth.getSession()).data.session;
    if (!current) {
      setProfile(null);
      setStartupError('');
      return;
    }
    try {
      const nextProfile = await api<Profile>('/api/me');
      setProfile(nextProfile);
      setStartupError('');
    } catch (error) {
      setProfile(null);
      setStartupError(error instanceof Error ? error.message : 'Unable to load your MAGNA Accounting profile.');
    }
  };

  useEffect(() => {
    let alive = true;
    supabase.auth.getSession().then(async ({ data }) => {
      if (!alive) return;
      setSession(data.session);
      if (data.session) await refreshProfile();
      setLoading(false);
    }).catch((error) => {
      if (!alive) return;
      setStartupError(error instanceof Error ? error.message : 'Unable to initialize authentication.');
      setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange(async (_event, next) => {
      if (!alive) return;
      setSession(next);
      if (next) await refreshProfile();
      else {
        setProfile(null);
        setStartupError('');
      }
      setLoading(false);
    });

    return () => {
      alive = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthValue>(() => ({
    session,
    profile,
    loading,
    startupError,
    refreshProfile,
    signOut: async () => {
      setStartupError('');
      await supabase.auth.signOut();
    },
    hasRole: (...roles) => !!profile?.roles?.some((role) => roles.includes(role.code)),
  }), [session, profile, loading, startupError]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider');
  return value;
}
